import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  IRegistroImportacaoItinerario,
  SITUACAO_CEP_NAO_ENCONTRADO,
} from 'src/app/modules/qco/models/item-itinerario.interface';
import {
  formatarKm,
  IRegistroImportacaoPontoParada,
} from 'src/app/modules/qco/models/ponto-parada.interface';

/** Registro das verificações com endereço e "Cadastrar na base" (PBIs 112887 e 113368). */
type RegistroVerificacaoEndereco =
  | IRegistroImportacaoItinerario
  | IRegistroImportacaoPontoParada;

/** Origens cuja verificação usa a tabela própria, com checkbox por linha. */
const ORIGENS_TABELA_ENDERECO = ['itinerarios', 'pontos-parada'];

@Component({
  selector: 'app-import-validation',
  templateUrl: './import-validation.component.html',
  styleUrls: ['./import-validation.component.scss'],
})
export class ImportValidationComponent {
  dataSource: any[] = [];
  displayedColumns: string[] = [];
  columnConfig: any[] = [];
  fileName = '';
  totalRows = 0;
  isValid = true;
  response: any;
  from = '';
  modalTitle: string = 'Importação de Veículos';
  errorMsg: string =
    'Erro: Foram encontradas inconsistências na importação do veiculo';

  /**
   * Registros da verificação do itinerário (PBI 112887) ou dos pontos de parada
   * (PBI 113368), na ordem recebida do back (não encontrados primeiro).
   */
  registrosItinerario: RegistroVerificacaoEndereco[] = [];
  readonly colunasItinerario: string[] = [
    'numLinha',
    'ordem',
    'cep',
    'logradouro',
    'bairro',
    'municipio',
    'estado',
    'latitude',
    'longitude',
    'situacaoEndereco',
    'cadastrarNaBase',
  ];
  /** Pontos de parada: as colunas do itinerário com Número e Km (contrato 113368 §1.1). */
  readonly colunasPontosParada: string[] = [
    'numLinha',
    'ordem',
    'cep',
    'logradouro',
    'bairro',
    'municipio',
    'estado',
    'latitude',
    'longitude',
    'numero',
    'km',
    'situacaoEndereco',
    'cadastrarNaBase',
  ];
  readonly formatarKm = formatarKm;

  /** Verificação válida de itinerário ou de pontos de parada: tabela com checkbox. */
  get usaTabelaEndereco(): boolean {
    return this.isValid && ORIGENS_TABELA_ENDERECO.includes(this.from);
  }

  get colunasTabelaEndereco(): string[] {
    return this.from === 'pontos-parada'
      ? this.colunasPontosParada
      : this.colunasItinerario;
  }

  constructor(
    private dialogRef: MatDialogRef<ImportValidationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.response = this.data.resp;
    this.fileName = this.data.file.name;
    this.isValid = this.response.valido;
    this.from = this.data.from;

    if (this.data.modalTitle) {
      this.modalTitle = this.data.modalTitle;
    }

    if (this.data.errorMsg) {
      this.errorMsg = this.data.errorMsg;
    }

    if (this.usaTabelaEndereco) {
      // PBIs 112887 e 113368: a verificação do itinerário e a dos pontos de
      // parada têm checkbox por linha, que o app-table-default não renderiza.
      // A tabela própria lê os registros originais, então a marca de
      // "Cadastrar na base" volta para quem abriu o modal em
      // `data.resp.registrosValidos`.
      this.registrosItinerario = this.response.registrosValidos ?? [];
      this.totalRows = this.registrosItinerario.length;
    } else if (this.isValid) {
      this.dataSource = this.flattenDados(this.response.registrosValidos);
      this.totalRows = this.dataSource.length;
      this.setColumnConfig();
    } else {
      this.dataSource = this.response.erros;
      this.totalRows = this.response.erros.length;
      this.columnConfig = [
        {
          columnDef: 'numLinha',
          header: 'Número da linha',
          property: 'numLinha',
        },
        {
          columnDef: 'mensagem',
          header: 'Descrição de erro',
          property: 'mensagem',
        },
      ];
    }

    this.displayedColumns = this.columnConfig.map((c) => c.columnDef);
  }

  /** A API devolve cada registro como { numLinha, dados: { ... } }; o table-default
   * resolve `property` direto na raiz da linha. */
  private flattenDados(registros: any[]): any[] {
    return (registros ?? []).map((registro) => ({
      ...registro,
      ...registro.dados,
    }));
  }

  /** O checkbox "Cadastrar na base" só existe para CEP ausente da base (§1.1). */
  podeCadastrarNaBase(registro: RegistroVerificacaoEndereco): boolean {
    return registro.situacaoEndereco === SITUACAO_CEP_NAO_ENCONTRADO;
  }

  onCadastrarNaBaseChange(
    registro: RegistroVerificacaoEndereco,
    marcado: boolean,
  ): void {
    registro.cadastrarNaBase = this.podeCadastrarNaBase(registro) && marcado;
  }

  onImport() {
    this.dialogRef.close(true);
  }

  back() {
    this.dialogRef.close(false);
  }
  onReset() {
    this.dialogRef.close({ retry: true });
  }

  getResponsiveFileName(nome: string): string {
    const largura = window.innerWidth;

    let limiteCaracteres = 100;

    if (largura <= 768) {
      limiteCaracteres = 25;
    }
    if (largura <= 480) {
      limiteCaracteres = 15;
    }

    return nome.length > limiteCaracteres
      ? nome.substring(0, limiteCaracteres) + '...'
      : nome;
  }

  setColumnConfig() {
    switch (this.from) {
      case 'veiculos':
        this.columnConfig = [
          {
            columnDef: 'numLinha',
            header: 'Número da linha',
            property: 'numLinha',
          },
          {
            columnDef: 'numeroVeiculo',
            header: 'Número Veiculo',
            property: 'numeroVeiculo',
          },
          { columnDef: 'placa', header: 'Placa', property: 'placa' },
          {
            columnDef: 'consorcio',
            header: 'Consórcio',
            property: 'consorcio',
          },
          {
            columnDef: 'celulaOperacional',
            header: 'Célula Operacional',
            property: 'celulaOperacional',
          },
          { columnDef: 'padrao', header: 'Padrão', property: 'padrao' },
          {
            columnDef: 'modeloCarroceria',
            header: 'Modelo Carroceria',
            property: 'modeloCarroceria',
          },
          {
            columnDef: 'anoCarroceria',
            header: 'Ano Carroceria',
            property: 'anoCarroceria',
          },
          {
            columnDef: 'modeloChassi',
            header: 'Modelo Chassi',
            property: 'modeloChassi',
          },
          {
            columnDef: 'anoChassi',
            header: 'Ano Chassi',
            property: 'anoChassi',
          },
          { columnDef: 'chassi', header: 'Chassi', property: 'chassi' },
          { columnDef: 'tanque1', header: 'Tanque 1', property: 'tanque1' },
          { columnDef: 'tanque2', header: 'Tanque 2', property: 'tanque2' },
          {
            columnDef: 'comprimentoAntesRoleta',
            header: 'Comprimento Antes Roleta',
            property: 'comprimentoAntesRoleta',
          },
          {
            columnDef: 'comprimentoAposRoleta',
            header: 'Comprimento Após Roleta',
            property: 'comprimentoAposRoleta',
          },
          {
            columnDef: 'comprimentoAreaDeficiente',
            header: 'Comprimento Área Deficiente',
            property: 'comprimentoAreaDeficiente',
          },
          {
            columnDef: 'comprimentoVeiculo',
            header: 'Comprimento Veículo',
            property: 'comprimentoVeiculo',
          },
          { columnDef: 'cor', header: 'Cor', property: 'cor' },
          {
            columnDef: 'dataEntradaSistema',
            header: 'Data Entrada Sistema',
            property: 'dataEntradaSistema',
          },
          {
            columnDef: 'dataInstalacaoElevador',
            header: 'Data Instalação Elevador',
            property: 'dataInstalacaoElevador',
          },
          {
            columnDef: 'dataBaixa',
            header: 'Data Baixa',
            property: 'dataBaixa',
          },
          {
            columnDef: 'dataSeguro',
            header: 'Data Seguro',
            property: 'dataSeguro',
          },
          {
            columnDef: 'dataLacre',
            header: 'Data Lacre',
            property: 'dataLacre',
          },
          {
            columnDef: 'dataTransferenciaCelulaOperacional',
            header: 'Data Transferência Célula Operacional',
            property: 'dataTransferenciaCelulaOperacional',
          },
          {
            columnDef: 'distanciaEixos',
            header: 'Distância Eixos',
            property: 'distanciaEixos',
          },
          {
            columnDef: 'docEntrada',
            header: 'Documento Entrada',
            property: 'docEntrada',
          },
          {
            columnDef: 'larguraAntesRoleta',
            header: 'Largura Antes Roleta',
            property: 'larguraAntesRoleta',
          },
          {
            columnDef: 'larguraAposRoleta',
            header: 'Largura Após Roleta',
            property: 'larguraAposRoleta',
          },
          {
            columnDef: 'larguraPortaCentral',
            header: 'Largura Porta Central',
            property: 'larguraPortaCentral',
          },
          {
            columnDef: 'larguraPortaCentralEsquerda',
            header: 'Largura Porta Central Esquerda',
            property: 'larguraPortaCentralEsquerda',
          },
          {
            columnDef: 'larguraPortaDianteira',
            header: 'Largura Porta Dianteira',
            property: 'larguraPortaDianteira',
          },
          {
            columnDef: 'larguraPortaDianteiraEsquerda',
            header: 'Largura Porta Dianteira Esquerda',
            property: 'larguraPortaDianteiraEsquerda',
          },
          {
            columnDef: 'larguraPortaTraseira',
            header: 'Largura Porta Traseira',
            property: 'larguraPortaTraseira',
          },
          {
            columnDef: 'larguraPortaTraseiraEsquerda',
            header: 'Largura Porta Traseira Esquerda',
            property: 'larguraPortaTraseiraEsquerda',
          },
          {
            columnDef: 'larguraAreaDeficiente',
            header: 'Largura Área Deficiente',
            property: 'larguraAreaDeficiente',
          },
          {
            columnDef: 'localMotor',
            header: 'Local Motor',
            property: 'localMotor',
          },
          {
            columnDef: 'seguradora',
            header: 'Seguradora',
            property: 'seguradora',
          },
          {
            columnDef: 'codConsorcio',
            header: 'Código Consórcio',
            property: 'codConsorcio',
          },
          {
            columnDef: 'lacreRoleta',
            header: 'Lacre Roleta',
            property: 'lacreRoleta',
          },
          {
            columnDef: 'lacreAssoalho',
            header: 'Lacre Assoalho',
            property: 'lacreAssoalho',
          },
          {
            columnDef: 'lacrePescoco',
            header: 'Lacre Pescoço',
            property: 'lacrePescoco',
          },
          { columnDef: 'renavam', header: 'Renavam', property: 'renavam' },
          {
            columnDef: 'numVeiculoSubstituido',
            header: 'Nº Veículo Substituído',
            property: 'numVeiculoSubstituido',
          },
          { columnDef: 'peso', header: 'Peso', property: 'peso' },
          {
            columnDef: 'potenciaMotor',
            header: 'Potência Motor',
            property: 'potenciaMotor',
          },
          {
            columnDef: 'validadeLicenca',
            header: 'Validade Licença',
            property: 'validadeLicenca',
          },
          {
            columnDef: 'validadeRegistro',
            header: 'Validade Registro',
            property: 'validadeRegistro',
          },
          { columnDef: 'assentos', header: 'Assentos', property: 'assentos' },
          {
            columnDef: 'qtdPortas',
            header: 'Qtd. Portas',
            property: 'qtdPortas',
          },
          {
            columnDef: 'qtdPortasEsquerda',
            header: 'Qtd. Portas Esquerda',
            property: 'qtdPortasEsquerda',
          },
          {
            columnDef: 'qtdValidadores',
            header: 'Qtd. Validadores',
            property: 'qtdValidadores',
          },
          {
            columnDef: 'arCondicionado',
            header: 'Ar Condicionado',
            property: 'arCondicionado',
          },
          { columnDef: 'cobrador', header: 'Cobrador', property: 'cobrador' },
          {
            columnDef: 'cameraVideo',
            header: 'Câmera Vídeo',
            property: 'cameraVideo',
          },
          { columnDef: 'elevador', header: 'Elevador', property: 'elevador' },
          { columnDef: 'gps', header: 'GPS', property: 'gps' },
          {
            columnDef: 'roletaEletronica',
            header: 'Roleta Eletrônica',
            property: 'roletaEletronica',
          },
          {
            columnDef: 'suporteBicicleta',
            header: 'Suporte Bicicleta',
            property: 'suporteBicicleta',
          },
          {
            columnDef: 'televisao',
            header: 'Televisão',
            property: 'televisao',
          },
          {
            columnDef: 'validador',
            header: 'Validador',
            property: 'validador',
          },
          { columnDef: 'wifi', header: 'Wi-Fi', property: 'wifi' },
          {
            columnDef: 'tipoAssento',
            header: 'Tipo Assento',
            property: 'tipoAssento',
          },
          { columnDef: 'cambio', header: 'Câmbio', property: 'cambio' },
          {
            columnDef: 'codigoTipoOperacao',
            header: 'Código Tipo Operação',
            property: 'codigoTipoOperacao',
          },
          {
            columnDef: 'suspensao',
            header: 'Suspensão',
            property: 'suspensao',
          },
          {
            columnDef: 'tipoVeiculo',
            header: 'Tipo Veículo',
            property: 'tipoVeiculo',
          },
        ];
        break;

      // 'itinerarios' e 'pontos-parada' têm tabela própria no template
      // (registrosItinerario + colunasTabelaEndereco).
      case 'mcos':
        this.columnConfig = [
          {
            columnDef: 'numLinha',
            header: 'Número da linha',
            property: 'numLinha',
          },
          {
            columnDef: 'tripId',
            header: 'ID Viagem',
            property: 'tripId',
          },
          {
            columnDef: 'vehicleId',
            header: 'ID Veículo',
            property: 'vehicleId',
          },
          {
            columnDef: 'lineId',
            header: 'ID Atendimento',
            property: 'lineId',
          },
          {
            columnDef: 'datetimeProc',
            header: 'Data e Hora Programada',
            property: 'datetimeProc',
          },
          {
            columnDef: 'directionTrip',
            header: 'Direção',
            property: 'directionTrip',
          },
          {
            columnDef: 'dateTrip',
            header: 'Data da Viagem',
            property: 'dateTrip',
          },
          {
            columnDef: 'hourInitialTrip',
            header: 'Hora Inicial',
            property: 'hourInitialTrip',
          },
          {
            columnDef: 'hourFinalTrip',
            header: 'Hora Final',
            property: 'hourFinalTrip',
          },
          {
            columnDef: 'durationTrip',
            header: 'Duração',
            property: 'durationTrip',
          },
          {
            columnDef: 'totalPassengers',
            header: 'Total Passageiros',
            property: 'totalPassengers',
          },
          {
            columnDef: 'initialTurnstile',
            header: 'Roleta Inicial',
            property: 'initialTurnstile',
          },
          {
            columnDef: 'finalTurnstile',
            header: 'Roleta Final',
            property: 'finalTurnstile',
          },
          {
            columnDef: 'lineDescShort',
            header: 'Descrição da Linha',
            property: 'lineDescShort',
          },
          {
            columnDef: 'demandaVt',
            header: 'Demanda Vale-Transporte',
            property: 'demandaVt',
          },
          {
            columnDef: 'demandaCidadao',
            header: 'Demanda Cidadão',
            property: 'demandaCidadao',
          },
          {
            columnDef: 'demandaGratuidade',
            header: 'Demanda Gratuidade',
            property: 'demandaGratuidade',
          },
          {
            columnDef: 'demandaEstudante',
            header: 'Demanda Estudante',
            property: 'demandaEstudante',
          },
          {
            columnDef: 'demandaFuncionario',
            header: 'Demanda Funcionário',
            property: 'demandaFuncionario',
          },
          {
            columnDef: 'demandaDinheiro',
            header: 'Demanda Dinheiro',
            property: 'demandaDinheiro',
          },
          {
            columnDef: 'demandaOutros',
            header: 'Demanda Outros',
            property: 'demandaOutros',
          },
          {
            columnDef: 'demandaIntegracaoIoo',
            header: 'Demanda Integração IOO',
            property: 'demandaIntegracaoIoo',
          },
          {
            columnDef: 'demandaIntegracaoIom',
            header: 'Demanda Integração IOM',
            property: 'demandaIntegracaoIom',
          },
          {
            columnDef: 'demandaIntegracaoImo',
            header: 'Demanda Integração IMO',
            property: 'demandaIntegracaoImo',
          },
          {
            columnDef: 'demandaIntegracaoIco',
            header: 'Demanda Integração ICO',
            property: 'demandaIntegracaoIco',
          },
          {
            columnDef: 'receitaTotal',
            header: 'Receita Total',
            property: 'receitaTotal',
            nativePipe: 'currency',
          },
          {
            columnDef: 'receitaIntegracaoIoo',
            header: 'Receita Integração IOO',
            property: 'receitaIntegracaoIoo',
            nativePipe: 'currency',
          },
          {
            columnDef: 'receitaIntegracaoIom',
            header: 'Receita Integração IOM',
            property: 'receitaIntegracaoIom',
            nativePipe: 'currency',
          },
          {
            columnDef: 'receitaIntegracaoImo',
            header: 'Receita Integração IMO',
            property: 'receitaIntegracaoImo',
            nativePipe: 'currency',
          },
          {
            columnDef: 'receitaIntegracaoIco',
            header: 'Receita Integração ICO',
            property: 'receitaIntegracaoIco',
            nativePipe: 'currency',
          },
          {
            columnDef: 'receitaNaoIntegracao',
            header: 'Receita Não Integração',
            property: 'receitaNaoIntegracao',
            nativePipe: 'currency',
          },
          {
            columnDef: 'receitaVt',
            header: 'Receita Vale-Transporte',
            property: 'receitaVt',
            nativePipe: 'currency',
          },
          {
            columnDef: 'receitaCidadao',
            header: 'Receita Cidadão',
            property: 'receitaCidadao',
            nativePipe: 'currency',
          },
          {
            columnDef: 'receitaGratuidade',
            header: 'Receita Gratuidade',
            property: 'receitaGratuidade',
            nativePipe: 'currency',
          },
          {
            columnDef: 'receitaEstudante',
            header: 'Receita Estudante',
            property: 'receitaEstudante',
            nativePipe: 'currency',
          },
          {
            columnDef: 'receitaFuncionario',
            header: 'Receita Funcionário',
            property: 'receitaFuncionario',
            nativePipe: 'currency',
          },
          {
            columnDef: 'receitaDinheiro',
            header: 'Receita Dinheiro',
            property: 'receitaDinheiro',
            nativePipe: 'currency',
          },
          {
            columnDef: 'receitaOutros',
            header: 'Receita Outros',
            property: 'receitaOutros',
            nativePipe: 'currency',
          },
          {
            columnDef: 'horaRetorno',
            header: 'Data de Retorno',
            property: 'horaRetorno',
          },
          {
            columnDef: 'demandaRetorno',
            header: 'Demanda Retorno',
            property: 'demandaRetorno',
          },
        ];
        break;

      default:
        break;
    }
  }
}
